import type { ReactNode } from "react"
import Tooltip from "@/v2-ui/tooltip"
import { DotText } from "@/v2-backoffice-client/components/dot"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import {
  getIdentityStatusConfig,
  getIdentityHealthyDisplay,
} from "@/v2-backoffice-client/user/identity/userIdentity.constants"

export type UserIdentityBadgeProps = {
  user: UserFragment
  // When true (the detail panel) the healthy "In sync" / "Not yet checked"
  // state is rendered too. In the table we leave it null so only problem
  // accounts draw the eye.
  showHealthy?: boolean
  // Optional repair affordance rendered at the foot of the tooltip (the
  // detail panel passes the fix button here so the action sits next to the
  // explanation of what's wrong). Tooltip content is hoverable, so it stays
  // clickable. Only shows when there's a problem tooltip to attach to.
  action?: ReactNode
}

// UserIdentityBadge surfaces how a user's DB record relates to their Azure AD
// B2C identity (computed by the nightly reconciliation). The tooltip shows the
// exact divergence — DB vs Azure email/name — and which repair action to use.
function UserIdentityBadge(props: UserIdentityBadgeProps) {
  const { user, showHealthy, action } = props

  const config = getIdentityStatusConfig(user.IdentitySyncStatus)
  const nameMismatch = user.IdentityNameMismatch

  if (!config && !nameMismatch) {
    if (!showHealthy) return null
    const healthy = getIdentityHealthyDisplay(user.IdentitySyncStatus)
    return <DotText variant={healthy.variant}>{healthy.label}</DotText>
  }

  const variant = config?.variant ?? "muted"
  const label = config?.label ?? "Name differs"
  const dbName = formatUserName(user.FirstName, user.LastName)

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <DotText variant={variant}>{label}</DotText>
      </Tooltip.Trigger>

      <Tooltip.Content side="top" className="ui-tooltip-content--panel">
        <div className="flex max-w-xs flex-col gap-3 p-3">
          {config && (
            <div className="flex flex-col gap-1">
              <DotText
                variant={variant}
                className="flex font-medium text-popover-foreground"
              >
                {config.label}
              </DotText>
              <p className="text-muted-foreground">{config.description}</p>

              {user.IdentityLoginEmail && (
                <div className="mt-1 flex flex-col gap-0.5 text-xs">
                  <span>
                    <span className="text-muted-foreground">Database: </span>
                    {user.Email}
                  </span>
                  <span>
                    <span className="text-muted-foreground">Azure login: </span>
                    {user.IdentityLoginEmail}
                  </span>
                </div>
              )}

              <p className="mt-1 text-xs text-muted-foreground">{config.fix}</p>
            </div>
          )}

          {nameMismatch && (
            <div className="flex flex-col gap-1">
              {!config && (
                <DotText
                  variant="muted"
                  className="flex font-medium text-popover-foreground"
                >
                  Name differs
                </DotText>
              )}
              <p className="text-xs">
                <span className="text-muted-foreground">Database: </span>
                {dbName}
                <br />
                <span className="text-muted-foreground">Azure: </span>
                {user.IdentityDisplayName}
              </p>
            </div>
          )}

          {action && config && (
            <div className="border-t border-border pt-2">{action}</div>
          )}
        </div>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default UserIdentityBadge
